import apiClient from './apiClient';
import { ProjectMember, ApiResponse, ApiError } from '../types/api';

export class ProjectMemberService {
  /**
   * Get all members for a specific project
   * @param projectId - The ID of the project
   * @param estimatedMonth - Optional date (YYYY-MM-DD) to determine applicable salary rate
   * @param includeAllocations - Whether to include allocations from pbs_projects_allocations (default: true)
   * @returns Promise with members array and optional budget/costs data
   */
  static async getProjectMembers(
    projectId: number, 
    estimatedMonth?: string,
    includeAllocations: boolean = true
  ): Promise<{
    members: ProjectMember[];
    hasAllocations?: boolean;
    budget?: any;
    projectCosts?: any[];
  }> {
    try {
      let url = `/projects/${projectId}/members`;
      const params = new URLSearchParams();
      if (estimatedMonth) {
        params.append('estimated_month', estimatedMonth);
      }
      if (includeAllocations !== undefined) {
        params.append('include_allocations', includeAllocations.toString());
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await apiClient.get<any>(url);
      
      // Handle response format - backend returns { data: [...], hasAllocations: bool, budget: {...}, projectCosts: [...] }
      if (response.data) {
        // Check if it's the new format with data property
        if (response.data.data && Array.isArray(response.data.data)) {
          return {
            members: response.data.data,
            hasAllocations: response.data.hasAllocations || false,
            budget: response.data.budget,
            projectCosts: response.data.projectCosts || []
          };
        }
        
        // Check if it's a direct array (old format)
      if (Array.isArray(response.data)) {
          return {
            members: response.data,
            hasAllocations: false
          };
      }
        
        // Check if it's wrapped in ApiResponse
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          if (Array.isArray(data)) {
            return {
              members: data,
              hasAllocations: false
            };
          }
        }
      }
      
      return {
        members: [],
        hasAllocations: false
      };
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Add a member to a project with allocation
   * @param projectId - The ID of the project
   * @param memberId - The ID of the member to add
   * @param allocatedHours - Hours allocated for the member
   * @param weekStartDate - Start date of the week
   * @returns Promise<ProjectMember> - Created project member allocation
   */
  static async addProjectMember(
    projectId: number,
    memberId: number,
    allocatedHours: number,
    weekStartDate: string
  ): Promise<ProjectMember> {
    try {
      const response = await apiClient.post<ApiResponse<ProjectMember>>(`/projects/${projectId}/members`, {
        member_id: memberId,
        allocated_hours: allocatedHours,
        week_start_date: weekStartDate,
      });
      return response.data.data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Update member allocation for a project
   * @param projectId - The ID of the project
   * @param memberId - The ID of the member
   * @param allocatedHours - Updated hours allocated
   * @param weekStartDate - Start date of the week
   * @returns Promise<ProjectMember> - Updated project member allocation
   */
  static async updateProjectMemberAllocation(
    projectId: number,
    memberId: number,
    allocatedHours: number,
    weekStartDate: string
  ): Promise<ProjectMember> {
    try {
      const response = await apiClient.put<ApiResponse<ProjectMember>>(`/projects/${projectId}/members/${memberId}`, {
        allocated_hours: allocatedHours,
        week_start_date: weekStartDate,
      });
      return response.data.data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Remove a member from a project
   * @param projectId - The ID of the project
   * @param memberId - The ID of the member to remove
   * @returns Promise<void>
   */
  static async removeProjectMember(projectId: number, memberId: number): Promise<void> {
    try {
      await apiClient.delete(`/projects/${projectId}/members/${memberId}`);
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Get project member allocation for a specific week
   * @param projectId - The ID of the project
   * @param weekStartDate - Start date of the week
   * @returns Promise<ProjectMember[]> - Array of project members for the week
   */
  static async getProjectMembersByWeek(projectId: number, weekStartDate: string): Promise<ProjectMember[]> {
    try {
      const response = await apiClient.get<ApiResponse<ProjectMember[]> | ProjectMember[]>(
        `/projects/${projectId}/members/week?week_start_date=${encodeURIComponent(weekStartDate)}`
      );
      // Handle both wrapped and direct array responses
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as ApiResponse<ProjectMember[]>).data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Save project allocations to pbs_projects_allocations
   * @param projectId - The ID of the project
   * @param allocations - Array of allocations with user_id, monthlyAllocations, hourly_rate, client_hourly_rate
   * @param projectBudget - Optional budget data
   * @param projectCosts - Optional project costs array
   * @param syncRoster - True when `allocations` is the grid's full team, so the
   *                     backend can persist members added to / removed from it
   * @returns Promise with success status
   */
  static async saveProjectAllocations(
    projectId: number,
    allocations: Array<{
      user_id: number;
      monthlyAllocations: { [monthStart: string]: number };
      hourly_rate: number;
      client_hourly_rate?: number;
    }>,
    projectBudget?: any,
    projectCosts?: any[],
    syncRoster: boolean = false
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post<ApiResponse<{ success: boolean; message?: string }>>(
        `/projects/${projectId}/allocations`,
        {
          allocations,
          projectBudget,
          projectCosts,
          // The grid posts its whole team, so the backend can persist adds/removes
          syncRoster
        }
      );
      return response.data.data || { success: true };
    } catch (error) {
      throw error as ApiError;
    }
  }
}
